import { useState, useCallback, useRef, useEffect } from 'react';
import type {
  Angle,
  ClientInspectionState,
  InspectionReport,
  UUID,
} from '../types/api';
import { REQUIRED_ANGLES } from '../types/api';
import {
  createInspection,
  getBatchStatus,
  getInspectionReport,
  generateIdempotencyKey,
  createInspectionMetadata,
  ApiError,
  getAngleLabel,
} from '../api';

interface UseInspectionOptions {
  deviceId: string;
  appVersion: string;
  apiKey?: string;
}

export interface UseInspectionReturn {
  state: ClientInspectionState;
  // Plate confirmation
  setPlate: (plate: string) => void;
  confirmPlate: () => boolean;
  // Photo capture
  capturePhoto: (angle: Angle, file: File) => void;
  removePhoto: (angle: Angle) => void;
  // Video capture (motor→escape, opcional)
  captureVideo: (file: File) => void;
  removeVideo: () => void;
  setCurrentAngle: (index: number) => void;
  nextAngle: () => void;
  prevAngle: () => void;
  // Upload
  uploadInspection: () => Promise<{ success: boolean; inspectionId?: UUID; error?: string }>;
  // Status polling
  startPolling: (inspectionId: UUID) => void;
  stopPolling: () => void;
  // Report
  fetchReport: (inspectionId: UUID) => Promise<InspectionReport | null>;
  // Reset
  reset: () => void;
}

const initialState: ClientInspectionState = {
  plate: '',
  photos: {},
  currentAngleIndex: 0,
  isUploading: false,
  uploadProgress: 0,
};

export function useInspection({
  deviceId,
  appVersion,
  apiKey,
}: UseInspectionOptions): UseInspectionReturn {
  const [state, setState] = useState<ClientInspectionState>(initialState);
  const pollingIntervalRef = useRef<number | null>(null);

  // Plate management
  const setPlate = useCallback((plate: string) => {
    setState((prev) => ({ ...prev, plate: plate.toUpperCase() }));
  }, []);

  const confirmPlate = useCallback(() => {
    setState((prev) => ({
      ...prev,
      plate: prev.plate.toUpperCase(),
      currentAngleIndex: 0,
      error: undefined,
    }));
    return true;
  }, []);

  // Photo capture
  const capturePhoto = useCallback((angle: Angle, file: File) => {
    const preview = URL.createObjectURL(file);
    setState((prev) => ({
      ...prev,
      photos: {
        ...prev.photos,
        [angle]: { file, preview, timestamp: Date.now() },
      },
      error: undefined,
    }));
  }, []);

  const removePhoto = useCallback((angle: Angle) => {
    setState((prev) => {
      const photo = prev.photos[angle];
      if (photo?.preview) {
        URL.revokeObjectURL(photo.preview);
      }
      const { [angle]: removed, ...rest } = prev.photos;
      return { ...prev, photos: rest };
    });
  }, []);

  // Video capture (motor→escape) — substitui o vídeo anterior, se houver
  const captureVideo = useCallback((file: File) => {
    const preview = URL.createObjectURL(file);
    setState((prev) => {
      if (prev.video?.preview) URL.revokeObjectURL(prev.video.preview);
      return { ...prev, video: { file, preview, timestamp: Date.now() }, error: undefined };
    });
  }, []);

  const removeVideo = useCallback(() => {
    setState((prev) => {
      if (prev.video?.preview) URL.revokeObjectURL(prev.video.preview);
      return { ...prev, video: undefined };
    });
  }, []);

  // Angle navigation
  const setCurrentAngle = useCallback((index: number) => {
    setState((prev) => ({
      ...prev,
      currentAngleIndex: Math.max(0, Math.min(index, REQUIRED_ANGLES.length - 1)),
    }));
  }, []);

  const nextAngle = useCallback(() => {
    setState((prev) => ({
      ...prev,
      currentAngleIndex: Math.min(prev.currentAngleIndex + 1, REQUIRED_ANGLES.length - 1),
    }));
  }, []);

  const prevAngle = useCallback(() => {
    setState((prev) => ({
      ...prev,
      currentAngleIndex: Math.max(prev.currentAngleIndex - 1, 0),
    }));
  }, []);

  // Upload
  const uploadInspection = useCallback(async () => {
    const { plate, photos } = state;
    
    if (!plate) {
      return { success: false, error: 'Placa não informada' };
    }

    const missingAngles = REQUIRED_ANGLES.filter((angle: Angle) => !photos[angle]);
    if (missingAngles.length > 0) {
      return { success: false, error: `Faltam fotos: ${missingAngles.map(getAngleLabel).join(', ')}` };
    }

    const idempotencyKey = generateIdempotencyKey();
    const metadata = createInspectionMetadata(deviceId, appVersion);

    const photoFiles: Record<Angle, File> = {} as Record<Angle, File>;
    for (const angle of REQUIRED_ANGLES as Angle[]) {
      const photo = photos[angle];
      if (photo) {
        photoFiles[angle] = photo.file;
      }
    }

    const videoFile = state.video?.file;

    setState((prev) => ({ ...prev, isUploading: true, uploadProgress: 0, error: undefined }));

    try {
      // Simulate progress updates (since we can't track multipart progress easily)
      const progressInterval = setInterval(() => {
        setState((prev) => ({
          ...prev,
          uploadProgress: Math.min(prev.uploadProgress + 10, 90),
        }));
      }, 200);

      const result = await createInspection(
        { plate, metadata, photos: photoFiles, video: videoFile },
        { apiKey, idempotencyKey }
      );

      clearInterval(progressInterval);
      setState((prev) => ({
        ...prev,
        isUploading: false,
        uploadProgress: 100,
        inspectionId: result.inspection_id,
        serverStatus: 'processing',
        serverUpdatedAt: result.received_at,
      }));

      return { success: true, inspectionId: result.inspection_id };
    } catch (error) {
      clearInterval(pollingIntervalRef.current || 0);
      
      if (error instanceof ApiError) {
        if (error.code === 'duplicate' && error.inspectionId) {
          setState((prev) => ({
            ...prev,
            isUploading: false,
            uploadProgress: 100,
            inspectionId: error.inspectionId,
            serverStatus: 'processing',
          }));
          return { success: true, inspectionId: error.inspectionId };
        }
        
        setState((prev) => ({
          ...prev,
          isUploading: false,
          uploadProgress: 0,
          error: error.message,
          errorCode: error.code,
        }));
        return { success: false, error: error.message };
      }
      
      setState((prev) => ({
        ...prev,
        isUploading: false,
        uploadProgress: 0,
        error: 'Erro de conexão. Tente novamente.',
      }));
      return { success: false, error: 'Erro de conexão. Tente novamente.' };
    }
  }, [state.plate, state.photos, state.video, deviceId, appVersion, apiKey]);

  // Status polling
  const startPolling = useCallback((inspectionId: UUID) => {
    stopPolling();
    
    const poll = async () => {
      try {
        const results = await getBatchStatus([inspectionId], apiKey);
        const item = results[0];
        
        if (item) {
          setState((prev) => ({
            ...prev,
            serverStatus: item.status,
            serverUpdatedAt: item.updated_at,
            failureReason: item.reason,
          }));

          if (item.status === 'done' || item.status === 'failed') {
            stopPolling();
          }
        }
      } catch (error) {
        console.error('Polling error:', error);
        // Continue polling on error
      }
    };

    // Initial poll
    poll();
    
    // Poll every 3 seconds
    pollingIntervalRef.current = window.setInterval(poll, 3000);
  }, [apiKey]);

  const stopPolling = useCallback(() => {
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
      pollingIntervalRef.current = null;
    }
  }, []);

  // Fetch report
  const fetchReport = useCallback(async (inspectionId: UUID): Promise<InspectionReport | null> => {
    try {
      const report = await getInspectionReport(inspectionId, apiKey);
      setState((prev) => ({ ...prev, report }));
      return report;
    } catch (error) {
      if (error instanceof ApiError) {
        setState((prev) => ({ ...prev, error: error.message }));
      }
      return null;
    }
  }, [apiKey]);

  // Reset
  const reset = useCallback(() => {
    stopPolling();
    // Revoke all preview URLs
    Object.values(state.photos).forEach((photo) => {
      if (photo?.preview) URL.revokeObjectURL(photo.preview);
    });
    if (state.video?.preview) URL.revokeObjectURL(state.video.preview);
    setState(initialState);
  }, [state.photos, state.video, stopPolling]);

  // Refs para o cleanup — previews revogados APENAS no unmount. Com o
  // InspectionProvider o hook vive durante todo o fluxo cliente; revogar a
  // cada mudança de `photos` quebraria as miniaturas já capturadas.
  const photosRef = useRef(state.photos);
  const videoRef = useRef(state.video);
  photosRef.current = state.photos;
  videoRef.current = state.video;

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      stopPolling();
      Object.values(photosRef.current).forEach((photo) => {
        if (photo?.preview) URL.revokeObjectURL(photo.preview);
      });
      if (videoRef.current?.preview) URL.revokeObjectURL(videoRef.current.preview);
    };
  }, [stopPolling]);

  return {
    state,
    setPlate,
    confirmPlate,
    capturePhoto,
    removePhoto,
    captureVideo,
    removeVideo,
    setCurrentAngle,
    nextAngle,
    prevAngle,
    uploadInspection,
    startPolling,
    stopPolling,
    fetchReport,
    reset,
  };
}

